import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  CancelToken,
  Method,
  ResponseType,
} from "axios";
import { AuthContext } from "../utility/useAuth";
import { clearAuthCache } from "../utility/authCache";
import { logger } from "../utility/logger";
import { User } from "oidc-client-ts";
import { IOperation } from "../types/IOperation";
import { IJob } from "../types/IJob";
import { FeedbackSubmission } from "../types/FeedbackSubmission";
import { FeedbackSubmissionResult } from "../types/FeedbackSubmissionResult";
import { getConfig } from "../config";
import { MAX_CHECKED_GROUPS } from "../utility/corpAccessGroups";
import { SEAT_CAPS_ENABLED } from "../utility/seatCapFeature";
import { getBffRoot, getHomeBffRoot } from "./regionRuntime";
import { assistVocabulary } from "../reporting/columnLabels";
import { IUserInfo } from "../types/IUserInfo";
import { IJobDetails } from "../types/IJobDetails";
import { IBomDetails } from "../types/IBomDetails";
import { IPickedComponent } from "../types/IPickedComponent";
import { IRouting } from "../types/IRouting";
import { IStartJobRequest } from "../types/IStartJobRequest";
import { IJobLogging } from "../types/IJobLogging";
import { INonProductionJob } from "../types/INonProductionJob";
import { InspectionToolSet } from "../types/IInspectionToolSet";
import { InspectionStatus } from "../types/InspectionStatus";
import { ICompleteJobRequest } from "../types/ICompleteJobRequest";
import { InspectionLog } from "../types/InspectionLog";
import { LeakTestCertificate } from "../types/LeakTestCertificate";
import { WorkflowRequest } from "../types/WorkflowRequest";
import { WorkflowResponse } from "../types/WorkflowResponse";
import { EmailMessage } from "../types/EmailMessage";
import { IJobWithQtyOrdered } from "../types/IJobWithQtyOrdered";
import { IJobDataItem } from "../types/IJobDataItem";
import { PrintRequest } from "../types/PrintRequest";
import { Parameter } from "../types/Parameter";
import { ICheckList } from "../types/ICheckList";
import { Ticket } from "../types/reportChecklists/Ticket";
import { OperationFilter } from "../types/OperationFilter";
import { UserPreference } from "../types/UserPreference";
import { IJobDocuments } from "../types/IJobDocuments";
import { ExportControlAnswer, toFormFields } from "../utility/exportControl";
import {
  ColumnInfo,
  ReportQueryRequest,
  SavedReport,
  encodeDefinitionForTransport,
} from "../reporting/reportQuery";

export const REQUEST_CANCELLED = "REQUEST_CANCELLED";

/**
 * Raised when a request failed because the caller's TOKEN was rejected (401/403), as opposed
 * to a genuine authorization or licensing decision about the user. PrivateRoute keys off this
 * to send the user through a fresh login instead of rendering "Access Denied", which is what
 * an expired token used to surface as.
 */
export class TokenRejectedError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "TokenRejectedError";
    this.status = status;
  }
}

/**
 * Raised when the BFF has made a deliberate, final decision that this user may not use this
 * environment — as opposed to rejecting their token. The distinction is load-bearing:
 * `isTokenRejection` below counts 403 as a token rejection, and PrivateRoute answers a token
 * rejection by redirecting to /login. A licensing denial sent as a bare 403 would therefore
 * bounce the user straight back into the IdP, which returns them authenticated, which denies
 * them again — a login loop instead of a message telling them what is wrong.
 *
 * The BFF marks these responses with a machine-readable `code` so the client can be certain
 * it is a decision and not an accident; see `Endpoints/GetUser.cs` in the Function App.
 */
export class AccessDeniedError extends Error {
  readonly status: number;

  /** e.g. "CORP_GROUP_REQUIRED" — the specific rule that refused the caller. */
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "AccessDeniedError";
    this.status = status;
    this.code = code;
  }
}

/**
 * Error codes the BFF uses to mark a licensing denial (see AccessDeniedError).
 *
 * Every `403 { code }` the BFF can return MUST be listed here. A 403 whose code is missing
 * falls through to `isTokenRejection`, which reads it as a stale token and redirects to
 * `/login` — where the IdP hands back a perfectly valid token and the same 403 comes straight
 * back. That is a login loop, not an error message.
 */
const ACCESS_DENIED_CODES = new Set([
  // Not in this environment's Entra access group (CSM-NP-QA-Swg-access / CSM-P-Swg-access).
  "CORP_GROUP_REQUIRED",
  // Graph companyName is not one of the corp-capable Swagelok companies. A corp login is
  // licensed by two live Entra facts, and a dbo.Users row with WHOTO=CORP is neither of them.
  "CORP_COMPANY_REQUIRED",
  // NOTE: SUPPORT_KNOWLEDGE_MANAGER_REQUIRED was removed on 2026-09-14 along with the
  // assistant's self-writing answer cache and the manager-only endpoint that read it. The
  // support assistant now returns no 403 of any kind — a rating for somebody else's exchange
  // is a 404 on purpose, precisely so it cannot be mistaken for a licensing denial. If that
  // ever changes, the new code belongs in this set before the endpoint ships.
]);

/**
 * True when the BFF refused this user on purpose. MUST be checked BEFORE
 * `isTokenRejection`, which would otherwise claim the same 403.
 *
 * A type predicate rather than a plain boolean so callers can read `.code` off the narrowed
 * error — PrivateRoute needs it to tell CORP_COMPANY_REQUIRED from CORP_GROUP_REQUIRED, which
 * are two different problems wearing the same 403.
 */
export function isAccessDenied(error: any): error is AccessDeniedError { // eslint-disable-line
  return error instanceof AccessDeniedError;
}

/**
 * Recognize a licensing denial in a raw axios error and convert it. Returns the original
 * error untouched when the response is anything else, so callers can rethrow blindly.
 */
function asAccessDenied(error: any): any { // eslint-disable-line
  const status = error?.response?.status;
  if (status !== 403) return error;

  const body = error.response?.data;
  const code = typeof body?.code === "string" ? body.code : undefined;

  if (!code || !ACCESS_DENIED_CODES.has(code)) return error;

  return new AccessDeniedError(
    status,
    code,
    typeof body?.message === "string"
      ? body.message
      : "This account is not licensed for this environment."
  );
}

/** True for anything that means "this token is no longer accepted" — ours or axios's. */
export function isTokenRejection(error: any): boolean { // eslint-disable-line
  if (error instanceof TokenRejectedError) return true;
  if (error instanceof AccessDeniedError) return false; // a decision, not a stale token
  const status = error?.response?.status;
  return status === 401 || status === 403;
}

// Set once the app has given up on silent renewal and started a full interactive login.
// A page mount fires many requests at once, so without this every one of them would race
// its own signinRedirect; the first to arrive wins and the rest just surface their error.
let interactiveReauthStarted = false;

/** Test seam — module state would otherwise leak between cases. */
export function __resetInteractiveReauth(): void {
  interactiveReauthStarted = false;
}

/**
 * Last-resort recovery when silent renewal fails: send the user through a full top-level
 * interactive login. That redirect is a normal document navigation, so unlike the silent
 * iframe it is unaffected by third-party-cookie policy or `frame-src`.
 *
 * Before this existed, a failed renew simply rejected and the page never loaded — the user
 * had to close the browser, which worked only because it cleared sessionStorage. This does
 * the same thing automatically.
 */
function startInteractiveReauth(auth: AuthContext): void {
  if (interactiveReauthStarted) return;
  interactiveReauthStarted = true;

  // The cached decision outlives the token, so drop it before we re-enter the flow.
  clearAuthCache();

  Promise.resolve()
    .then(() => auth.login())
    .catch((loginError) => {
      // Nothing further to try; let the next mount fall through to the /login route.
      interactiveReauthStarted = false;
      logger.error("Interactive login redirect failed.", loginError);
    });
}

const encode = (id: string) => encodeURIComponent(encodeURIComponent(id));

// `bffRoot` is resolved DYNAMICALLY (getBffRoot) so the ~80 `${config.bffRoot}api/...`
// call sites below transparently target the ACTIVE region's Function App in the
// single-SWA / multi-FA model. Every other config field passes through unchanged.
const config = new Proxy(getConfig(), {
  get(target, prop, receiver) {
    if (prop === "bffRoot") return getBffRoot();
    return Reflect.get(target, prop, receiver);
  },
});

let ambientEmail: string | null = null;

/**
 * The Function App hosting the CALLER's own data. Deliberately not the `config` proxy above:
 * it must not follow the active region, which moves when a CORP user is placed in the org
 * they support. Used by the handful of calls that are about the PERSON rather than that org
 * (TOS acceptance, getSupportTenant/setSupportTenant), all of which also pass
 * `{ ambient: "self" }`.
 *
 * For a corp-capable user this is the default `bffRoot` (their companyName maps to no
 * region); for a regular user it is their home region's FA — which is NOT the default, so
 * e.g. a PJAYA operator's own row is still read from the ASIA database.
 */
const homeBffRoot = (): string => getHomeBffRoot();

/**
 * Microsoft Graph /me lookup (companyName, etc.) using the caller's access token.
 * Kept in the service layer so components (e.g. PrivateRoute) don't issue their own
 * raw cross-origin fetches. Graph is a different host than the BFF, so it is called
 * directly with the bearer token rather than through the `call<T>` helper.
 */
export async function getGraphMe(accessToken: string): Promise<any> {
  const response = await fetch(
    "https://graph.microsoft.com/v1.0/me?$select=id,displayName,mail,userPrincipalName,companyName",
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  if (!response.ok) {
    // A rejected token is reported distinctly: this is a raw fetch with no renew/retry of
    // its own, and PrivateRoute calls it FIRST, so an expired token failing here used to be
    // swallowed by PrivateRoute's catch-all and rendered as "Authentication Error: Access
    // Denied" — a licensing message for what is really just a stale token.
    if (response.status === 401 || response.status === 403) {
      throw new TokenRejectedError(
        response.status,
        `Graph /me rejected the access token (${response.status}).`
      );
    }
    throw new Error(`Graph /me request failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Microsoft Graph `POST /me/checkMemberGroups` — of the group ids passed in, which is the
 * caller actually a member of? Used to decide whether a CORP user may use THIS environment
 * (see utility/corpAccessGroups.ts).
 *
 * Three things make this the right call for the job rather than `/me/memberOf`:
 *  - For the SIGNED-IN user it needs only **User.Read**, the delegated permission this app
 *    already relies on for `getGraphMe`. No new admin consent, on either half of the gate.
 *  - It is **transitive**, so membership through a nested group counts — which is how these
 *    access groups are usually populated.
 *  - It answers in one small round trip regardless of how many groups the user is in, so it
 *    is also the fix for the ID-token overage case (a user in 200+ groups gets no `groups`
 *    claim at all).
 *
 * Graph caps the request at 20 ids and fails the WHOLE request on an invalid collection, so
 * an over-long list is truncated rather than sent — one group per environment is expected.
 *
 * Callers must treat a throw as "unknown", never as "not a member": both halves of this gate
 * fail closed on an indeterminate answer, and the user sees "we couldn't verify your access"
 * rather than an accusation about their account.
 */
export async function getGraphGroupMembership(
  accessToken: string,
  groupIds: string[]
): Promise<string[]> {
  if (groupIds.length === 0) return [];

  const checked = groupIds.slice(0, MAX_CHECKED_GROUPS);

  if (groupIds.length > checked.length) {
    logger.warn(
      `More than ${MAX_CHECKED_GROUPS} access groups are configured; only the first ${MAX_CHECKED_GROUPS} are checked.`
    );
  }

  const response = await fetch(
    "https://graph.microsoft.com/v1.0/me/checkMemberGroups",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ groupIds: checked }),
    }
  );

  if (!response.ok) {
    // Same reasoning as getGraphMe: a rejected token must not be reported as a licensing
    // decision, or a merely expired session renders as "Access Denied".
    if (response.status === 401 || response.status === 403) {
      throw new TokenRejectedError(
        response.status,
        `Graph checkMemberGroups rejected the access token (${response.status}).`
      );
    }
    throw new Error(`Graph checkMemberGroups failed: ${response.status}`);
  }

  const body = await response.json();

  return Array.isArray(body?.value)
    ? body.value.filter((id: unknown): id is string => typeof id === "string")
    : [];
}

// ============================================================================
// TOS (Terms of Service) acceptance
//
// Accepting the terms is an act of the PERSON, not of the tenant they happen to be
// connected to, so — like getSupportTenant/setSupportTenant below — these two calls
// pin BOTH the identity and the region rather than following the session:
//
//   * `ambient: "self"` omits X-Ambient-Email. Once a CORP user is impersonating,
//     the default would make the BFF read/write SUPPORT_<ORG>USER's TosAcceptedDate —
//     a row shared by every CORP user and different in every org, so the 30-day
//     window would restart whenever the org they were restored into changed.
//   * `homeBffRoot()` instead of `config.bffRoot`, which resolves to the ACTIVE
//     region: the acceptance lives on the user's own row in their own region's
//     database, and a supported org can be in another one (PJAYA ⇒ ASIA DB).
//
// Both matter because login now restores the last supported org BEFORE the TOS gate
// renders (PrivateRoute → applySupportTarget → Home → TOSPage/Login), so without
// these the prompt reappears instead of honouring the 30-day acceptance.
// ============================================================================

export async function getTosStatus(
  auth: AuthContext
): Promise<{ accepted: boolean; acceptedDate: string | null }> {
  const val = await call<{ accepted: boolean; acceptedDate: string | null }>(
    `${homeBffRoot()}api/tos-status`,
    "GET",
    auth,
    undefined,
    undefined,
    undefined,
    { ambient: "self" }
  );
  return val.data;
}

export async function acceptTos(
  auth: AuthContext
): Promise<{ accepted: boolean; acceptedDate: string | null }> {
  const val = await call<{ accepted: boolean; acceptedDate: string | null }>(
    `${homeBffRoot()}api/tos-accept`,
    "POST",
    auth,
    undefined,
    undefined,
    undefined,
    { ambient: "self" }
  );
  return val.data;
}

/* -----------------------------------------------------------------------------
 * Operations Details DTOs (new)
 * -------------------------------------------------------------------------- */
export interface OperationDetails {
  resourceNo: string;               // Operation code (id)
  resourceAlias: string;            // Display/alias (can be same as code if desired)
  resourceDescription: string;
  sortOrder: number;
  rules?: Record<string, string>;   // Optional for now
}

export interface SaveOperationsResponse {
  operations?: OperationDetails[];  // Some backends return { operations: [...] }
}

// Re-export IUserInfo so pages can import it directly from ApiService if they want
export type { IUserInfo } from "../types/IUserInfo";

export interface CallOptions {
  /**
   * Whose identity the BFF should resolve the request as.
   *  - "session" (default): send the current impersonation override in `X-Ambient-Email`,
   *    so the request runs in whatever tenant the user is currently connected to.
   *  - "self": omit the header entirely, so the BFF falls back to the caller's own token
   *    identity. Needed for the handful of calls that are ABOUT the person rather than
   *    about the tenant they are supporting — see getSupportTenant/setSupportTenant.
   */
  ambient?: "session" | "self";

  /**
   * Abandon the request after this many ms. Omitted (the default) means NO timeout, which
   * is right for everything that talks only to the BFF: those either answer or fail.
   *
   * Set it on the calls that go THROUGH the BFF to Saltbox and on into SAP B1. Those hold a
   * connection open for as long as the ERP takes, and a connection that dies quietly leaves
   * an axios promise that never settles — on the write-back board that is the global loader
   * spinning with no error, no toast and no way out but navigating away, because the
   * workflow's `finally` never runs. See SALTBOX_TIMEOUT_MS.
   */
  timeoutMs?: number;
}

/**
 * Network timeout for the B1 write-back calls (issue / report completion / close / resync).
 *
 * Deliberately ABOVE every server-side ceiling in the chain, so it can only ever fire on a
 * connection that is actually dead — never on one that is merely slow. The Function App's
 * HttpClient gives up on Saltbox at 100s and answers 500, and Azure's front end caps an HTTP
 * trigger at 230s and answers 502; both of those are responses, and the workflow's existing
 * catch handles them. This is the backstop for the case where no response arrives at all.
 *
 * Getting this WRONG in the other direction would be worse than the bug it guards: a close
 * that succeeded in B1, timed out here, and was reported to the operator as a failure would
 * send them to SAP to "fix" an order that was already correct.
 */
export const SALTBOX_TIMEOUT_MS = 240000;

export const call = async <Response = any>( // eslint-disable-line
  path: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  auth: AuthContext,
  payload?: any, // eslint-disable-line
  cancelToken?: CancelToken,
  responseType?: string,
  options?: CallOptions
): Promise<AxiosResponse<Response>> => {
  const requestConfig: AxiosRequestConfig = {
    method: method as Method,
    url: `${path}`,
    data: payload,
    cancelToken,
    responseType: responseType as ResponseType,
    // Only set when the caller asked for one — axios reads 0 as "no timeout", which is
    // also the default, but being explicit here would hide that distinction from anyone
    // reading the config. Carried on the shared object, so the post-renew retry inherits it.
    ...(options?.timeoutMs ? { timeout: options.timeoutMs } : {}),
  };

  // Resolved per attempt (including the post-renew retry) so impersonation survives a
  // token refresh: dropping the header there would silently run the retried request as
  // the CORP user instead of the tenant they are supporting, and return the wrong
  // tenant's data. `ambient: "self"` omits the header so the BFF uses the token identity.
  const ambientHeaders = (): Record<string, string> =>
    options?.ambient === "self" ? {} : { "X-Ambient-Email": ambientEmail ?? "" };

  return auth
    .getUser()
    .then((user: User | null) => {
      if (user && user.access_token) {
        axios.defaults.headers.common.Accept = "application/json";
        axios.defaults.headers.common.Authorization = `Bearer ${user.access_token}`;
        requestConfig.headers = {
          Accept: "application/json",
          Authorization: `Bearer ${user.access_token}`,
          ...ambientHeaders(),
        };


        return axios.request<Response>(requestConfig);
      }
      throw new Error("User has no access token");
    })
    .catch((error) => {
      if (!error.response) throw error; // Network error (disconnected)
      if (error.response.status === 401) {
        // 401 Unauthorized — renew silently and retry once. If the renew itself fails there
        // is no way to recover in-page, so escalate to a full interactive login rather than
        // rejecting: a bare rejection here is what left pages permanently blank until the
        // user closed the browser.
        return auth
          .renewToken()
          .then((renewedUser) => {
            const headers = {
              Accept: "application/json",
              Authorization: `Bearer ${renewedUser.access_token}`,
              ...ambientHeaders(),
            };
            requestConfig.headers = headers;
            return axios.request<Response>(requestConfig);
          })
          .catch((renewError) => {
            // A 401 on the RETRY means the fresh token was refused too — that is a real
            // authorization problem, not an expiry, so don't bounce the user through login.
            if (renewError?.response?.status === 401) throw renewError;

            logger.warn(
              "Silent token renewal failed; escalating to an interactive login.",
              renewError
            );
            startInteractiveReauth(auth);
            throw new TokenRejectedError(
              401,
              "Access token expired and silent renewal failed; signing in again."
            );
          });
      }
      throw error;
    });
};

export async function getOperations(
  auth: AuthContext,
  filter?: string
): Promise<IOperation[]> {
  const val = await call<IOperation[]>(
    `${config.bffRoot}api/operations${filter ? `?filter=${filter}` : ""}`,
    "GET",
    auth
  );

  return val.data;
}

/**
 * Set (or clear) the support impersonation sent as `X-Ambient-Email`. Pass null — or "" —
 * to clear it, which makes the BFF resolve the caller's own token identity: that is how a
 * CORP user acts as themselves in their own dedicated environment, and how switching back
 * from another org drops that org's impersonation.
 */
export function setAmbientEmail(email: string | null) {
  ambientEmail = email;
}

export async function getJobs(
  auth: AuthContext,
  warehouse: string | null
): Promise<IJob[]> {
  const val = await call<IJob[]>(
    `${config.bffRoot}api/jobs${warehouse ? `?warehouse=${warehouse}` : ""}`,
    "GET",
    auth
  );
  return val.data;
}

/**
 * ONE board card — the same shape as an element of `getJobs`, for a single job.
 *
 * Use this instead of `getJobs` whenever you only need one job. `getJobs` returns
 * the whole board and, server-side, fans out to the tenant-wide enrichment
 * queries; at APAC's size that is thousands of rows to answer a question about
 * one production order.
 *
 * **Resolves to `null` when the job is not on a board**, which is a real answer
 * rather than a failure: it is how the write-back board learns a card has left.
 *
 * That is recognised by the `code` on the 404 body, NOT by the status alone.
 * Azure Functions answers a bare 404 for a route it doesn't recognise, so
 * deploying this app against a Function App that predates the endpoint would
 * otherwise report every job as gone — a permanently dead ribbon on the job
 * page, and a write-back board that deletes every card it polls. An
 * unrecognised 404 therefore THROWS, and callers keep whatever they had.
 */
const JOB_NOT_ON_BOARD_CODE = "JOB_NOT_ON_BOARD";

export async function getJobCard(
  auth: AuthContext,
  jobNumber: string
): Promise<IJob | null> {
  try {
    const val = await call<IJob>(
      `${config.bffRoot}api/jobs/${encodeURIComponent(jobNumber)}/card`,
      "GET",
      auth
    );
    return val.data;
  } catch (error: any) { // eslint-disable-line
    if (
      error?.response?.status === 404 &&
      error?.response?.data?.code === JOB_NOT_ON_BOARD_CODE
    ) {
      return null;
    }
    throw error;
  }
}

export async function getRelationshipMap(
  auth: AuthContext,
  warehouse: string | null
): Promise<IJob[]> {
  const val = await call<IJob[]>(
    `${config.bffRoot}api/relationship-map${warehouse ? `?warehouse=${warehouse}` : ""}`,
    "GET",
    auth
  );
  return val.data;
}

export async function getWarehouses(auth: AuthContext): Promise<string[]> {
  const val = await call<string[]>(
    `${config.bffRoot}api/warehouses`,
    "GET",
    auth
  );
  return val.data;
}

export async function getStartedJobByUser(auth: AuthContext): Promise<IJob> {
  const val = await call<IJob>(
    `${config.bffRoot}api/jobs?status=started`,
    "GET",
    auth
  );
  return val.data;
}

export async function getNonProductionJobs(
  auth: AuthContext
): Promise<INonProductionJob[]> {
  const val = await call<INonProductionJob[]>(
    `${config.bffRoot}api/non-production-jobs`,
    "GET",
    auth
  );
  return val.data;
}

export async function getJobDetails(
  auth: AuthContext,
  jobNumber: string,
  dontCreate: boolean = false
): Promise<IJobDetails> {
  const val = await call<IJobDetails>(
    `${config.bffRoot}api/jobs/${jobNumber}/details?dontcreate=${dontCreate}`,
    "GET",
    auth
  );
  return val.data;
}

export interface UpdateJobDetailsRequest {
  routings: {
    resource: string;
    loggedMinutes: number;
  }[];
  note?: string;
}

export async function updateJobDetails(
  auth: AuthContext,
  jobNumber: string,
  data: UpdateJobDetailsRequest,
  dontCreate: boolean = false
): Promise<IJobDetails> {
  const val = await call<IJobDetails>(
    `${config.bffRoot}api/jobs/${jobNumber}/details?dontcreate=${dontCreate}`,
    "POST",
    auth,
    data // ✅ THIS is the missing piece
  );

  return val.data;
}


export async function getBomDetails(
  auth: AuthContext,
  jobNumber: string
): Promise<IBomDetails> {
  const val = await call<IBomDetails>(
    `${config.bffRoot}api/jobs/${jobNumber}/bom`,
    "GET",
    auth
  );
  return val.data;
}

export async function getRoutings(
  auth: AuthContext,
  jobNumber: string
): Promise<IRouting[]> {
  const val = await call<IRouting[]>(
    `${config.bffRoot}api/jobs/${jobNumber}/routings`,
    "GET",
   auth
  );
  return val.data;
}


export interface IProductionOrderRelation {
  productionOrderNumber: string;
  parentProductionOrderNumber: string;
  relationType: string;
}

/**
 * Production-order parent/child links.
 *
 * Pass `jobNumber` whenever you care about one order — it narrows the query to
 * the relations that order takes part in on either side. Omitting it returns
 * EVERY relation row in the tenant, which is what the job page used to do on
 * every job opened just to ask "has this one a parent or any children?".
 */
export async function getProductionOrderRelations(
  auth: AuthContext,
  jobNumber?: string
): Promise<IProductionOrderRelation[]> {
  const val = await call<IProductionOrderRelation[]>(
    `${config.bffRoot}api/production-orders/relations${
      jobNumber ? `?job=${encodeURIComponent(jobNumber)}` : ""
    }`,
    "GET",
    auth
  );

  return val.data;
}

export async function getJobOperations(
  auth: AuthContext,
  jobNumber: string,
  status?: string
): Promise<IJobLogging[]> {
  const val = await call<IJobLogging[]>(
    `${config.bffRoot}api/jobs/${jobNumber}/operations${
      status ? `?status=${status}` : ""
    }`,
    "GET",
    auth
  );
  return val.data;
}

export async function getSimilarJobs(
  auth: AuthContext,
  jobNumber: string,
  status?: string
): Promise<IJobWithQtyOrdered[]> {
  const val = await call<IJobWithQtyOrdered[]>(
    `${config.bffRoot}api/similar-jobs?job=${jobNumber}`,
    "GET",
    auth
  );
  return val.data;
}

export async function getJobDataItems(
  auth: AuthContext
): Promise<IJobDocuments[]> {
  const val = await call<IJobDocuments[]>(
    `${config.bffRoot}api/job-data-items`,
    "GET",
    auth
  );
  return val.data;
}

export async function getJobSummaries(
  auth: AuthContext,
  oneMonth?: boolean
): Promise<IJobDataItem[]> {
  const url = `${config.bffRoot}api/job-summaries${
    oneMonth ? "?oneMonth=true" : ""
  }`;

  const val = await call<IJobDataItem[]>(
    url,
    "GET",
    auth
  );

  return val.data;
}

export async function getJobPlanningSummaries(
  auth: AuthContext
): Promise<IJobDataItem[]> {
  const val = await call<IJobDataItem[]>(
    `${config.bffRoot}api/job-planning-summaries`,
    "GET",
    auth
  );
  return val.data;
}

export async function updateBomDetails(
  auth: AuthContext,
  jobNumber: string,
  components: IPickedComponent[]
): Promise<any> {
  const val = await call<IBomDetails>(
    `${config.bffRoot}api/jobs/${jobNumber}/bom`,
    "POST",
    auth,
    components
  );
  return val;
}

export async function startOperation(
  auth: AuthContext,
  jobNumber: string,
  operation: string,
  request: IStartJobRequest
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/operations/${encode(
      operation
    )}/start`,
    "POST",
    auth,
    request
  );
  return val;
}

export async function stopOperation(
  auth: AuthContext,
  jobNumber: string,
  operation: string
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/operations/${encode(
      operation
    )}/stop`,
    "POST",
    auth
  );
  return val;
}

export async function deleteOperation(
  auth: AuthContext,
  jobNumber: string,
  operation: string
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/operations/${encode(
      operation
    )}/delete`,
    "POST",
    auth
  );
  return val;
}

export async function reworkOperation(
  auth: AuthContext,
  jobNumber: string,
  operation: string,
  reason?: string,
  cascade: boolean = true
): Promise<any> {
  // The reason (mandatory in the UI, optional here so older callers still
  // compile) is recorded by the BFF against the rework so it can be reported on.
  //
  // `cascade` is what makes a single-resource rework possible.
  // `JobActionService.ReworkJob` reopens the resource it is given AND every
  // later completed one; `cascade=false` asks it to reopen ONLY the resource
  // this call names, so the popup can post exactly the set the operator ticked.
  //
  // It is sent only when false, which keeps the URL unchanged for every other
  // caller and means a BFF that predates the parameter degrades to the old
  // cascade instead of failing — more is reopened than was asked for, which is
  // the behaviour that shipped for years, rather than a rework that errors.
  const params: string[] = [];

  if (reason && reason.trim() !== "") {
    params.push(`reason=${encodeURIComponent(reason.trim())}`);
  }

  if (!cascade) {
    params.push("cascade=false");
  }

  const query = params.length > 0 ? `?${params.join("&")}` : "";

  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/operations/${encode(
      operation
    )}/rework${query}`,
    "POST",
    auth
  );
  return val;
}

export async function completeOperation(
  auth: AuthContext,
  jobNumber: string,
  operation: string,
  request: ICompleteJobRequest
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/operations/${encode(
      operation
    )}/complete`,
    "POST",
    auth,
    request
  );
  return val;
}

export async function notifyBoardReload(
  auth: AuthContext,
  jobNumbers: string[]
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/notify-board-reload`,
    "POST",
    auth,
    { jobNumbers }
  );
  return val;
}

export async function completeProductionOrder(
  auth: AuthContext,
  jobNumber: string,
  batch: string,
  quantity: number,
  whoto: string
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/complete`,
    "POST",
    auth,
    { batch, quantity }
  );
  return val;
}

export async function deleteCompleteProductionOrder(
  auth: AuthContext,
  jobNumber: string,
  whoto: string
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/complete`,
    "DELETE",
    auth
  );
  return val;
}

export async function getJobNote(
  auth: AuthContext,
  jobNumber: string
): Promise<string> {
  const val = await call<string>(
    `${config.bffRoot}api/jobs/${jobNumber}/note`,
    "GET",
    auth
  );
  return val.data;
}

export async function updateJobNote(
  auth: AuthContext,
  jobNumber: string,
  jobNote: string
): Promise<any> {
  const val = await call<IBomDetails>(
    `${config.bffRoot}api/jobs/${jobNumber}/note`,
    "POST",
    auth,
    jobNote
  );
  return val;
}

export async function getCurrentUser(auth: AuthContext): Promise<IUserInfo> {
  try {
    const val = await call<IUserInfo>(
      `${config.bffRoot}api/users/current`,
      "GET",
      auth
    );

    return val.data;
  } catch (error) {
    // The BFF enforces the CORP access group here too, and answers 403 with a `code`. Turn
    // that into an AccessDeniedError so PrivateRoute renders a denial instead of treating
    // the 403 as an expired token and redirecting into a login loop.
    throw asAccessDenied(error);
  }
}

/**
 * GET /api/users — the tenant's users.
 *
 * The BFF omits inactive (disabled) accounts unless asked, which is what every
 * caller but one wants: an inactive user must not show up in an inspector picker
 * or a report. Pass `includeDisabled` on the admin Users page only — a manager
 * has to be able to see who is inactive and switch them back on.
 */
export async function getUsers(
  auth: AuthContext,
  options?: { includeDisabled?: boolean }
): Promise<IUserInfo[]> {
  const query = options?.includeDisabled ? "?includeDisabled=true" : "";

  const val = await call<IUserInfo[]>(
    `${config.bffRoot}api/users${query}`,
    "GET",
    auth
  );

  return val.data;
}

/**
 * How many users at this site may hold access, and how many already do.
 *
 * `source` is the load-bearing field:
 *  - `"configured"` — `cap` is a number and the server is enforcing it.
 *  - `"unlimited"`  — this site has no limit. A DECISION.
 *  - `"unavailable"` — the shared list could not be read. Nothing is being enforced, and the
 *    page must say so rather than show a number. NOT the same as unlimited.
 *  - `"disabled"` — the whole feature is switched off (`utility/seatCapFeature.ts`). Nothing is
 *    enforced and the page shows NOTHING — not even "Seat limit unavailable", which would
 *    report a fault where there is none. THE CURRENT STATE.
 */
export interface UserAccessCap {
  readonly cap: number | null;
  readonly used: number;
  readonly source: "configured" | "unlimited" | "unavailable" | "disabled";
}

/** The one answer used while seat caps are parked. Frozen — it is handed out repeatedly. */
const SEAT_CAPS_OFF: UserAccessCap = Object.freeze({
  cap: null,
  used: 0,
  source: "disabled",
} as const);

/**
 * GET /api/users/access-cap — manager-gated.
 *
 * ⚠️ THIS NEVER THROWS, AND THAT IS DELIBERATE. It is decoration on a screen whose real job is
 * saving users: a network blip, an old Function App that has no such route (404), or a 403 must
 * not break the Users page or — worse — reach `PrivateRoute`, where a bare 403 is read as a
 * stale token and turns into a login loop. Any failure reads as `unavailable`, which is exactly
 * what the server itself answers when the list is unreadable, so there is one code path on the
 * page for "no number to show".
 *
 * ⚠️ PARKED. `SEAT_CAPS_ENABLED` is false, so this returns `"disabled"` without a request and
 * nothing downstream ever sees a cap. THIS IS THE ONLY PLACE THE CLIENT FLAG IS READ: UsersPage
 * keeps its full seat-limit logic, still driven entirely by `source`, so it cannot rot while
 * parked and comes back by flipping one boolean. Tests that mock this module are unaffected by
 * the flag, which is also deliberate — they still pin the behaviour of every `source`.
 */
export async function getUserAccessCap(auth: AuthContext): Promise<UserAccessCap> {
  if (!SEAT_CAPS_ENABLED) {
    return SEAT_CAPS_OFF;
  }

  try {
    const val = await call<UserAccessCap>(
      `${config.bffRoot}api/users/access-cap`,
      "GET",
      auth
    );

    const body = val.data;

    // A body that is present but not the shape we expect is treated as unavailable too —
    // showing "0 of null" would be worse than showing nothing.
    if (!body || typeof body.used !== "number") {
      return { cap: null, used: 0, source: "unavailable" };
    }

    return body;
  } catch (error) {
    logger.warn(
      "Could not read this site's user access cap; the Users page will not show a limit.",
      error
    );

    return { cap: null, used: 0, source: "unavailable" };
  }
}

/** POST /api/users — send the full collection (matches SetUserResources) */
export async function updateUsers(
  auth: AuthContext,
  users: ReadonlyArray<IUserInfo>
): Promise<void> {
  await call<void>(
    `${config.bffRoot}api/users`,
    "POST",
    auth,
    users
  );
}

export async function getBankHolidays(auth: AuthContext): Promise<Date[]> {
  const val = await call<Date[]>(
    `${config.bffRoot}api/bank-holidays`,
    "GET",
    auth
  );

  return val.data;
}

export async function getInspectionToolSets(
  auth: AuthContext,
  operation: string
): Promise<InspectionToolSet[]> {
  const val = await call<InspectionToolSet[]>(
    `${config.bffRoot}api/operations/${operation}/inspection-toolsets`,
    "GET",
    auth
  );
  return val.data;
}

export async function getInspectionReasons(
  auth: AuthContext,
  operation: string,
  status: InspectionStatus
): Promise<string[]> {
  const val = await call<string[]>(
    `${config.bffRoot}api/operations/${operation}/inspection-reasons?status=${InspectionStatus[status]}`,
    "GET",
    auth
  );
  return val.data;
}

export async function getJobInspectionLogs(
  auth: AuthContext,
  job: string,
  status: InspectionStatus
): Promise<InspectionLog[]> {
  const val = await call<InspectionLog[]>(
    `${config.bffRoot}api/jobs/${job}/inspection-logs?status=${InspectionStatus[status]}`,
    "GET",
    auth
  );
  return val.data;
}

export async function getLeakTestCertificates(
  auth: AuthContext
): Promise<LeakTestCertificate[]> {
  const val = await call<LeakTestCertificate[]>(
    `${config.bffRoot}api/leak-test-certificates`,
    "GET",
    auth
  );
  return val.data;
}

export async function getLeakTestCertificatesForProductionOrder(
  auth: AuthContext,
  job: string
): Promise<LeakTestCertificate[]> {
  const val = await call<LeakTestCertificate[]>(
    `${config.bffRoot}api/jobs/${job}/leak-test-certificates`,
    "GET",
    auth
  );
  return val.data;
}

export async function saveLeakTestCertificate(
  auth: AuthContext,
  certificate: LeakTestCertificate
): Promise<LeakTestCertificate> {
  const val = await call<LeakTestCertificate>(
    `${config.bffRoot}api/leak-test-certificates`,
    "POST",
    auth,
    certificate
  );
  return val.data;
}

export async function executeWorkflow(
  auth: AuthContext,
  jobNumber: string,
  operation: string,
  action: string,
  request: WorkflowRequest
): Promise<WorkflowResponse> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/operations/${encode(
      operation
    )}/${action}/evaluate`,
    "POST",
    auth,
    request
  );
  return val.data;
}

export async function sendEmail(
  auth: AuthContext,
  emailMessage: EmailMessage
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/emails`,
    "POST",
    auth,
    emailMessage
  );
  return val;
}

/**
 * The Submit Feedback drawer's intake — typed, and the only caller of `api/feedback`.
 *
 * Routes server-side: Bug/Support is addressed to ServiceNow's intake mailbox on the submission
 * email itself (ServiceNow opens the incident from it — there is no API call any more, and no
 * INC number comes back), Feedback/Enhancement creates an Azure DevOps work item, and the email
 * goes out either way. Built on `call()` so it inherits the 401 → renew → retry every other
 * endpoint gets.
 *
 * ⚠️ Read `emailSent` for success, not the truthiness of the response — see
 * FeedbackSubmissionResult. A ticketing outage is a 200.
 */
export async function submitFeedback(
  auth: AuthContext,
  submission: FeedbackSubmission
): Promise<FeedbackSubmissionResult> {
  const val = await call<FeedbackSubmissionResult>(
    `${config.bffRoot}api/feedback`,
    "POST",
    auth,
    submission
  );
  return val.data;
}

export async function sendNonSupportEmail(
  auth: AuthContext,
  emailMessage: EmailMessage
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/emails/non-support`,
    "POST",
    auth,
    emailMessage
  );
  return val;
}


export async function getDocuments(
  auth: AuthContext,
  prefix: string,
  whoto: string,
  folder?: string,
): Promise<string[]> {
  const url = `${config.bffRoot}api/documents/${whoto}`;

  const val = await call<string[]>(url, "GET", auth);
  return val.data;
}

export async function getDocumentContent(
  auth: AuthContext,
  folder: string,
  documentName: string,
  whoto:string,
  thumbnail?: boolean,
  ecFolder?: string,
): Promise<Blob> {
  const query = documentQuery({ thumbnail: thumbnail ? "true" : undefined, ecFolder });
  const url = `${config.bffRoot}api/documents/${whoto}/${encodeURIComponent(
    folder
  )}/${encodeURIComponent(documentName)}${query}`;

  // Assuming your `call` wrapper supports responseType "blob" as the last param
  const res = await call<Blob>(url, "GET", auth, null, undefined, "blob");

  // Some wrappers return Blob directly, others return { data: Blob }
  return (res as any)?.data ?? (res as unknown as Blob);
}

export async function getTemplateParameters(
  auth: AuthContext,
  folder: string,
  documentName: string,
  id: string
): Promise<Parameter[]> {
  const val = await call<Parameter[]>(
    `${config.bffRoot}api/documents/${folder}/${documentName}/template-parameters?id=${id}`,
    "GET",
    auth
  );
  return val.data;
}

export async function printTemplate(
  auth: AuthContext,
  request: PrintRequest
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/print`,
    "POST",
    auth,
    request,
    undefined,
    "blob"
  );
  return val.data;
}

export async function printTemplateAsPdf(
  auth: AuthContext,
  request: PrintRequest
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/print/pdf`,
    "POST",
    auth,
    request,
    undefined,
    "blob"
  );
  return val.data;
}

export async function downloadDocument(
  auth: AuthContext,
  folder: string,
  documentName: string,
  whoto:string
): Promise<any> {
  const url = `${config.bffRoot}api/documents/${whoto}/${encodeURIComponent(
    folder
  )}/${encodeURIComponent(documentName)}`;

  const val = await call<string[]>(url, "GET", auth);
  return val;
}

export async function deleteDocument(
  auth: AuthContext,
  folder: string,
  documentName: string,
  whoto:string,
  ecFolder?: string
): Promise<any> {
  const url = `${config.bffRoot}api/documents/${whoto}/${encodeURIComponent(
    folder
  )}/${encodeURIComponent(documentName)}${documentQuery({ ecFolder })}`;

  const val = await call<string[]>(url, "DELETE", auth);
  return val;
}

/**
 * Query string for the documents endpoints. `ecFolder` names the "Export Controlled
 * Technology - …" folder an export-controlled upload lives under (PathItem.ecFolder). It is a
 * query value, not part of the path, because `{folder}` is one route segment and an encoded
 * '/' in it does not survive the hop through the Functions host.
 */
function documentQuery(params: { thumbnail?: string; ecFolder?: string }): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${encodeURIComponent(v as string)}`);
  return parts.length ? `?${parts.join("&")}` : "";
}

/**
 * `exportControl` is the answer to the export-control check (utility/exportControl.ts), sent
 * as multipart fields beside the file. The Function App files an export-controlled upload
 * under its "Export Controlled Technology - …" folder and records who answered and when. Omit
 * it only for the app's own uploads (inspection reports, diagnostic screenshots) — every
 * upload a user picks or captures on the Images/Documents screens must carry one.
 */
export async function uploadDocument(
  auth: AuthContext,
  folder: string,
  documentName: string,
  documentContent: Blob,
  whoto:string,
  exportControl?: ExportControlAnswer
): Promise<any> {
  const formData = new FormData();
  formData.append("file", documentContent);
  if (exportControl) {
    for (const [name, value] of Object.entries(toFormFields(exportControl))) {
      formData.append(name, value);
    }
  }
  const val = await call<string[]>(
    `${config.bffRoot}api/documents/${whoto}/${folder}/${documentName}`,
    "POST",
    auth,
    formData
  );
  return val;
}

export async function getOption(
  auth: AuthContext,
  option: string
): Promise<string> {
  const val = await call<string>(
    `${config.bffRoot}api/options/${option}`,
    "GET",
    auth
  );
  return val.data;
}

export async function saveOption(
  auth: AuthContext,
  option: string,
  value: string
): Promise<string> {
  const val = await call<string>(
    `${config.bffRoot}api/options/${option}`,
    "POST",
    auth,
    value
  );
  return val.data;
}

export async function saveOptions(
  auth: AuthContext,
  option: string,
  options: readonly string[]
): Promise<string[]> {
  const url = `${config.bffRoot}api/options/${encodeURIComponent(option)}`;
  const val = await call<string[]>(
    url,
    "POST",
    auth,
    options // body: JSON array of strings
  );
  return val.data;
}

/**
 * Preferred endpoint: persist full operation details (code, alias, description, sortOrder, rules).
 * Response can be either OperationDetails[] or { operations: OperationDetails[] }.
 */
export async function saveOperations(
  auth: AuthContext,
  details: ReadonlyArray<OperationDetails>
): Promise<OperationDetails[]> {
  const url = `${config.bffRoot}api/operations`;
  const val = await call<OperationDetails[] | SaveOperationsResponse>(
    url,
    "POST",
    auth,
    details
  );
  const data: any = val.data;
  // Normalize both response shapes to OperationDetails[]
  if (Array.isArray(data)) return data as OperationDetails[];
  if (data && Array.isArray(data.operations)) return data.operations as OperationDetails[];
  // If API returns 204/empty, just echo the input to keep UI consistent
  return details.slice() as OperationDetails[];
}

export async function getOptions(
  auth: AuthContext,
  option: string
): Promise<string[]> {
  const val = await call<string[]>(
    `${config.bffRoot}api/options?option=${option}`,
    "GET",
    auth
  );
  return val.data;
}

export async function getProjectFolder(
  auth: AuthContext,
  stockCode: string
): Promise<string> {
  const val = await call<string>(
    `${config.bffRoot}api/project-folders/${stockCode}`,
    "GET",
    auth
  );
  return val.data;
}

export async function getCheckList(
  auth: AuthContext,
  checkList: string
): Promise<ICheckList> {
  const val = await call<ICheckList>(
    `${config.bffRoot}api/checklists/${checkList}`,
    "GET",
    auth
  );
  return val.data;
}

export async function getPurchaseOrders(auth: AuthContext): Promise<string[]> {
  const val = await call<string[]>(
    `${config.bffRoot}api/purchase-orders`,
    "GET",
    auth
  );
  return val.data;
}

//CreateTicket
export async function createTicket(
  auth: AuthContext,
  ticket: Ticket
): Promise<string> {
  const val = await call<string>(
    `${config.bffRoot}api/tickets`,
    "POST",
    auth,
    ticket
  );
  return val.data;
}

export async function lockAction(
  auth: AuthContext,
  action: string,
  maxminutes?: number
): Promise<any> {
  const val = await call<string>(
    `${config.bffRoot}api/actions/${action}${
      maxminutes ? `?maxminutes=${maxminutes}` : ""
    }`,
    "POST",
    auth
  );
  return val;
}

export async function unlockAction(
  auth: AuthContext,
  action: string
): Promise<any> {
  const val = await call<string>(
    `${config.bffRoot}api/actions/${action}`,
    "DELETE",
    auth
  );
  return val;
}

export async function getOperationFilters(
  auth: AuthContext
): Promise<OperationFilter[]> {
  const val = await call<OperationFilter[]>(
    `${config.bffRoot}api/operation-filters`,
    "GET",
    auth
  );
  return val.data;
}

export async function saveOperationFilters(
  auth: AuthContext,
  filters: OperationFilter[]
): Promise<any> {
  const val = await call<OperationFilter[]>(
    `${config.bffRoot}api/operation-filters`,
    "POST",
    auth,
    filters
  );
  return val;
}

export async function updateProductionOrder(
  auth: AuthContext,
  whoto: string,
  payload: Record<string, any>
): Promise<any> {
  return await call(
    `${config.bffRoot}api/update-production-order?whoto=${encodeURIComponent(whoto)}`,
    "POST",
    auth,
    payload
  );
}

export async function closeProductionOrder(
  auth: AuthContext,
  whoto: string,
  payload: Record<string, any>
): Promise<any> {
  return await call(
    `${config.bffRoot}api/close-production-order?whoto=${encodeURIComponent(whoto)}`,
    "POST",
    auth,
    payload,
    undefined,
    undefined,
    { timeoutMs: SALTBOX_TIMEOUT_MS }
  );
}

// Records who closed a production order onto its CompletedProductionOrders row.
// Call this ONLY after the SAP B1 close (closeProductionOrder) has succeeded.
// `auto` = true for the auto-writeback feature (logged as "auto"); false for a
// manual close on the Writeback screen (the acting user's code is resolved
// server-side from the ambient email).
export async function markProductionOrderClosed(
  auth: AuthContext,
  jobNumber: string,
  auto: boolean
): Promise<any> {
  return await call(
    `${config.bffRoot}api/jobs/${jobNumber}/mark-closed`,
    "POST",
    auth,
    { auto }
  );
}

export async function issueProductionOrder(
  auth: AuthContext,
  whoto: string,
  payload: Record<string, any>
): Promise<any> {
  return await call(
    `${config.bffRoot}api/issue-production-order?whoto=${encodeURIComponent(whoto)}`,
    "POST",
    auth,
    payload,
    undefined,
    undefined,
    { timeoutMs: SALTBOX_TIMEOUT_MS }
  );
}

export async function reportCompleteProductionOrder(
  auth: AuthContext,
  whoto: string,
  payload: Record<string, any>
): Promise<any> {
  return await call(
    `${config.bffRoot}api/report-complete-production-order?whoto=${encodeURIComponent(whoto)}`,
    "POST",
    auth,
    payload,
    undefined,
    undefined,
    { timeoutMs: SALTBOX_TIMEOUT_MS }
  );
}

export async function manualDataResync(
  auth: AuthContext,
  whoto: string
): Promise<any> {
  return await call(
    `${config.bffRoot}api/manual-data-resync/${whoto}`,
    "POST",
    auth
  );
}

export async function manualResyncBridge(
  auth: AuthContext,
  whoto: string,
  docNumbers?: string
): Promise<any> {
  return await call(
    `${config.bffRoot}api/manual-resync-bridge/${whoto}`,
    "POST",
    auth,
    docNumbers != null ? { docNumbers } : undefined,
    undefined,
    undefined,
    { timeoutMs: SALTBOX_TIMEOUT_MS }
  );
}


export async function getUserPreference(
  auth: AuthContext
): Promise<UserPreference> {
  const val = await call<UserPreference>(
    `${config.bffRoot}api/users/current/preference`,
    "GET",
    auth
  );
  return val.data;
}

export async function setUserPreference(
  auth: AuthContext,
  filters: UserPreference
): Promise<any> {
  const val = await call<UserPreference>(
    `${config.bffRoot}api/users/current/preference`,
    "POST",
    auth,
    filters
  );
  return val;
}

/* -----------------------------------------------------------------------------
 * CORP support auto-tenant: the org a CORP user last supported
 *
 * These two calls are deliberately different from every other call in this file, in
 * two ways, and both matter:
 *
 *  1. `homeBffRoot()` instead of `config.bffRoot`. The `config` Proxy above resolves
 *     `bffRoot` to the ACTIVE region, which is the region of the tenant being supported
 *     (APAC once the user switches to PJAYA). The saved preference lives on the CORP
 *     user's OWN row in their own region's database, so these calls must always go to
 *     the default Function App — which is the corp-capable user's home FA, since
 *     PrivateRoute resolves no region for them (see regionMap.ts).
 *
 *  2. `ambient: "self"`, which omits `X-Ambient-Email`. Otherwise, once impersonation is
 *     active, the backend would read and write SUPPORT_<ORG>USER's row in the supported
 *     tenant's database rather than the CORP user's own — saved under the wrong identity,
 *     in the wrong tenant, never found again. The endpoint ignores the header regardless
 *     (Endpoints/Users/SupportTenant.cs); this keeps the two ends consistent.
 *
 * The backend answers 403 unless the caller's WHOTO is CORP/CORP2 and the
 * SupportAutoTenantEnabled flag is on, so callers must treat failure as "no saved
 * tenant" rather than surfacing it as a hard error.
 * -------------------------------------------------------------------------- */

export interface SupportTenantResponse {
  /** Whether the backend has the feature enabled. */
  enabled: boolean;
  /** Org/WHOTO code the user last supported, or null if none saved. */
  tenant: string | null;
  /** Support user for that org (informational; the client owns the SUPPORT_ORGS table). */
  supportUser: string | null;
}

export async function getSupportTenant(
  auth: AuthContext
): Promise<SupportTenantResponse> {
  const val = await call<SupportTenantResponse>(
    `${homeBffRoot()}api/users/current/support-tenant`,
    "GET",
    auth,
    undefined,
    undefined,
    undefined,
    { ambient: "self" }
  );
  return val.data;
}

/** Persist the org the user is now supporting. Pass null to clear it. */
export async function setSupportTenant(
  auth: AuthContext,
  tenant: string | null
): Promise<SupportTenantResponse> {
  const val = await call<SupportTenantResponse>(
    `${homeBffRoot()}api/users/current/support-tenant`,
    "POST",
    auth,
    { tenant },
    undefined,
    undefined,
    { ambient: "self" }
  );
  return val.data;
}

/* -----------------------------------------------------------------------------
 * Capacity DTOs & APIs (ADDED)
 * -------------------------------------------------------------------------- */

export type ResourceCapacity = {
  resourceCode: string;
  date: string;              // ISO string (server expects DateTime)
  availableHours: number;    // decimal (C# decimal)
};

export type ResourceRecurrenceCapacity = {
  resourceCode: string;
  recurrenceRule: string;    // e.g., "FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,TU;UNTIL=20260331"
  from: string;              // ISO string (C# DateTime)
  to: string;                // ISO string (C# DateTime)
  availableHours: number;    // decimal (C# decimal)
  notes?: string;
};

export async function getResourceRecurrenceCapacities(
  auth: AuthContext
): Promise<ResourceRecurrenceCapacity[]> {
  const url = `${config.bffRoot}api/resource-recurrence-capacities`;
  const val = await call<ResourceRecurrenceCapacity[]>(url, "GET", auth);
  return val.data ?? [];
}

export async function updateResourceRecurrenceCapacity(
  auth: AuthContext,
  recurrenceCapacities: ReadonlyArray<ResourceRecurrenceCapacity>
): Promise<void> {
  const url = `${config.bffRoot}api/resource-recurrence-capacities`;
  await call<void>(url, "POST", auth, recurrenceCapacities);
}

export async function getResourceCapacities(
  auth: AuthContext
): Promise<ResourceCapacity[]> {
  const url = `${config.bffRoot}api/resource-capacities`;
  const val = await call<ResourceCapacity[]>(url, "GET", auth);
  return val.data ?? [];
}

/* -----------------------------------------------------------------------------
 * Ad-hoc report builder (schema-driven). All three run under the caller's
 * ambient-email session, so results are RLS-scoped server-side. See
 * reporting/reportQuery.ts for the request contract and CSM.Shared/DataQuery
 * (BFF) for the matching server types.
 * -------------------------------------------------------------------------- */

/** GET api/report/tables — the tables the caller may build reports on (denylist applied). */
export async function getReportTables(auth: AuthContext): Promise<string[]> {
  const val = await call<string[]>(
    `${config.bffRoot}api/report/tables`,
    "GET",
    auth
  );
  return val.data ?? [];
}

/** GET api/report/tables/{table}/columns — columns (name + SQL type) for one table. */
export async function getReportColumns(
  auth: AuthContext,
  table: string
): Promise<ColumnInfo[]> {
  const val = await call<ColumnInfo[]>(
    `${config.bffRoot}api/report/tables/${encodeURIComponent(table)}/columns`,
    "GET",
    auth
  );
  return val.data ?? [];
}

/** POST api/report/execute — run a builder-defined query and return the rows. */
export async function executeReportQuery(
  auth: AuthContext,
  request: ReportQueryRequest
): Promise<any[]> {
  const val = await call<any[]>(
    `${config.bffRoot}api/report/execute`,
    "POST",
    auth,
    request
  );
  return val.data ?? [];
}

/** GET api/report/saved — reports the caller can open (own + shared at their site). */
export async function getSavedReports(auth: AuthContext): Promise<SavedReport[]> {
  const val = await call<SavedReport[]>(
    `${config.bffRoot}api/report/saved`,
    "GET",
    auth
  );
  return val.data ?? [];
}

/** POST api/report/saved — create (id 0) or update (id > 0) a saved report. */
export async function saveReport(
  auth: AuthContext,
  report: SavedReport
): Promise<SavedReport> {
  // Send the definition base64-encoded so the WAF doesn't mistake the serialized
  // query for a SQL-injection attempt and block the save (the BFF decodes it).
  const payload: SavedReport = {
    ...report,
    definition: encodeDefinitionForTransport(report.definition),
  };
  const val = await call<SavedReport>(
    `${config.bffRoot}api/report/saved`,
    "POST",
    auth,
    payload
  );
  return val.data;
}

/** DELETE api/report/saved/{id} — delete a report the caller owns. */
export async function deleteSavedReport(auth: AuthContext, id: number): Promise<void> {
  await call<void>(
    `${config.bffRoot}api/report/saved/${encodeURIComponent(String(id))}`,
    "DELETE",
    auth
  );
}

export async function updateResourceCapacities(
  auth: AuthContext,
  resourceCapacities: ReadonlyArray<ResourceCapacity>
): Promise<void> {
  const url = `${config.bffRoot}api/resource-capacities`;
  await call<void>(url, "POST", auth, resourceCapacities);
}

/* -----------------------------------------------------------------------------
 * Support assistant (POST api/support/chat)
 * -------------------------------------------------------------------------- */

/** One prior turn of the conversation. The client holds the thread; the BFF holds none of it. */
export interface SupportChatTurn {
  role: "user" | "assistant";
  content: string;
}

/**
 * The outcome of one question, as a VALUE rather than an exception.
 *
 * Every failure mode of this endpoint is something the panel has to say out loud in a
 * different way — "not switched on here", "busy", "couldn't reach it" — so throwing would
 * only mean the component immediately re-deriving which case it was from a status code.
 */
/**
 * One screenshot travelling with an answer.
 *
 * ⚠️ `data` is a base64 `data:` URI, NOT a URL — the pictures are embedded in the response
 * rather than hosted, which is what lets `AnswerText` refuse anything that could make a
 * network request. It is also why a picture-bearing answer is ~100–600KB and is not cached;
 * the server bounds it to 3 images per answer.
 */
export interface SupportImage {
  key: string;
  caption: string;
  data: string;
}

/**
 * WHERE THE USER IS, sent with the question.
 *
 * Every field is a KEY or an identifier the app already shows on screen — never a path, never
 * a URL, never anything the model could turn into a destination. That asymmetry is the same
 * one `utility/routes.ts` describes from the other direction: the server may name a screen,
 * and only this client knows what a screen's address is.
 *
 * All optional. An older Function App ignores what it doesn't know, and a screen that isn't in
 * the screen table simply sends no `screenKey` — the question is still answerable.
 */
export interface SupportChatContext {
  /** `ScreenDescriptor.key` from `utility/routes.ts`. Absent on an unmapped screen. */
  screenKey?: string;

  /** The production order the user is looking at, if the screen is about one. */
  productionOrder?: string;

  /** The production order they are currently clocked in to, if any. */
  clockedInJob?: string;
}

/**
 * One production order drawn as a CARD inside an answer.
 *
 * ⚠️ POSITIONED BY A MARKER, AND LOOKED UP BY INDEX. `AnswerText`'s `CARD_LINE` matches
 * `[[card:1]]` and reads `cards[0]`; `key` is that same 1-based slot as a string. It is NOT the
 * production order number — an order number may contain `/` or `.` (the Function App's
 * `BotToolset.OrderNumberShape` permits them), and a marker the client's regex cannot match is
 * one the operator reads as literal text. `Support/Tools/CardGuard.cs` is the other half.
 *
 * ⚠️ THE MODEL AUTHORED NONE OF THESE FIELDS. It chose where the card goes and which order it
 * is about; every value came from the lookup the server had already made. Which is why
 * `AnswerText` must NOT run `renderInline` over any of them — see the note there.
 */
export interface SupportCard {
  /** `"order"` today. Carried so a second kind need not be inferred from populated fields. */
  kind: string;

  /** The 1-based slot, as a string. See the note above: never the order number. */
  key: string;

  /** The production order number. The card's heading. */
  title: string;

  /** The part and what it is, where either is known. */
  subtitle?: string;

  /** The one-line state sentence, verbatim from the payload the model read. */
  state: string;

  customer?: string;
  warehouse?: string;
  quantityOrdered?: number;
  quantityCompleted?: number;
  operationsTotal?: number;
  operationsComplete?: number;

  /** `yyyy-MM-dd`, already stripped of the server's two "no date" sentinels. */
  scheduledEndDate?: string;

  /**
   * ⚠️ TRI-STATE. `undefined`/`null` means NOT KNOWABLE — no scheduled end date, or nothing
   * open left to be late for — and the chip is then absent rather than reading "on time". A
   * finished order is not late; it *was* late, which is a different sentence and one the card
   * cannot make. Render only on an explicit `true`.
   */
  late?: boolean | null;

  onShopFloorBoard?: boolean;
  beingWorkedOnNow?: boolean;
}

/**
 * A screen the answer offers to take the user to.
 *
 * ⚠️ KEYS, NOT PATHS. `key` is resolved locally through `utility/routes.ts`, which owns the
 * address AND the access rule. The server strips anything the user cannot reach; the panel
 * checks again with `canReachScreen` before rendering the chip. Two independent defences, and
 * the reason a response can name a screen but cannot express a destination.
 *
 * ⚠️ IT IS `key`, NOT `screenKey`, AND THAT COST THE WHOLE FAMILY. This interface said
 * `screenKey` until 2026-09-14 while the Function App's `SupportLink` (`Support/AnswerActionCatalog.cs`)
 * has always serialised `{ key, param, label }` — so `screenFor(ref.screenKey)` read `undefined`
 * on every real response, resolved to no screen, and NOT ONE LINK CHIP WAS EVER RENDERED. Every
 * test here passed because every test mocked this file's own shape. That is the third instance
 * of that bug class in this feature, which is why `ApiService.supportContract.test.ts` now
 * restates the server's spellings literally.
 */
export interface SupportLinkRef {
  key: string;

  /** Fills the screen's `:param` — always a production order today. */
  param?: string;

  /**
   * The server's own wording for the chip. Carried for completeness and DELIBERATELY NOT
   * rendered: the panel writes `Open ${screen.title}` from its own screen table, so the words
   * on the button and the words on the screen it opens cannot drift apart.
   */
  label?: string;
}

/**
 * A modal the answer offers to open, PRE-FILLED. Resolved through `utility/supportActions`.
 *
 * ⚠️ THE ORDER NUMBER IS `param`, NOT `productionOrder`. Same break as `SupportLinkRef` above
 * and a worse one: reading the wrong field yielded `undefined`, which fell back to whatever
 * order the user happened to be looking at — right by luck on the job screen, wrong or empty
 * everywhere else. An action chip that opens a rework dialog on the wrong production order is
 * the worst failure mode this feature has.
 */
export interface SupportActionRef {
  key: string;

  /** Which production order to open it for. Falls back to the one in context. */
  param?: string;

  /** The server's label. Not rendered — see `SupportLinkRef.label`. */
  label?: string;

  /**
   * ⚠️ `"openModal"`, AND THE CLIENT REFUSES ANYTHING ELSE. The Function App's own comment says
   * "always openModal, so the client can refuse anything else" — so the panel actually does,
   * rather than trusting that it stays true. Absent is accepted (an older Function App sent no
   * kind); a PRESENT value that is not `openModal` renders no chip at all.
   */
  kind?: string;

  /** The screen key the dialog lives on. The panel uses its own table's `hostScreen` instead. */
  hostScreen?: string;
}

/**
 * A follow-up question the answer offers.
 *
 * ⚠️ `label` is not a paraphrase — it is the target corpus entry's own `### question`, resolved
 * server-side by `AnswerActionGuard` from the `[[next:id]]` marker. Submitting it verbatim is
 * what guarantees the follow-up lands on the entry it came from instead of merely near it, so
 * do not "tidy" the wording before sending it.
 *
 * `key` is that entry's id. It is carried for diagnostics and for a stable React key; the panel
 * submits `label`.
 */
export interface SupportNextRef {
  key: string;
  label: string;
}

/**
 * A create form the answer offers to FILL IN on a settings screen. Resolved through
 * `utility/supportSettings`.
 *
 * ⚠️ NOTHING HERE SAVES ANYTHING, AND THE PANEL SAYS SO IN THE THREAD. The values are put in
 * the boxes of the page's own Add form and left there; the record does not exist until the
 * manager presses **Update**. The client's half of that promise is that the only thing reachable
 * from this object is `setState` — see `pages/UsersPage/useSupportUserPrefill` and
 * `pages/OperationsPage/useSupportSettingsPrefill`, and the two `neverSaves` tests.
 *
 * ⚠️ `fields` IS A DICTIONARY, AND IT IS THE ONLY MODEL-WRITTEN PAYLOAD IN THIS FILE. That is
 * correct here and wrong for `SupportCard`, and the difference is what the values ARE: a card's
 * are a claim about the system's state, these are a transcription of the sentence the user just
 * typed, going into empty inputs they are about to read. The Function App validates every field
 * name against a per-key schema and every value for length and for `[`, `]` and line breaks
 * (`Support/Tools/AnswerActionGuard.cs`); `supportSettingsFields` filters them again here,
 * because neither side may assume the other ran.
 */
export interface SupportSettingsRef {
  key: string;

  /** The screen key the form lives on. The panel uses its own table's `hostScreen` instead. */
  hostScreen?: string;

  /** The server's label. Not rendered — see `SupportLinkRef.label`. */
  label?: string;

  /** The values to put in the boxes, by field name. Absent fields are left empty. */
  fields?: Record<string, string>;
}

export type SupportChatResult =
  | {
      kind: "answer";
      answer: string;
      escalate: boolean;
      /**
       * Screens, modals and follow-up questions the answer offers as chips.
       *
       * All three are absent on a Function App that predates them, and the answer then renders
       * as text alone — nothing here is required. All three are OBJECTS, not bare strings: a
       * follow-up carries the corpus id it points at as well as the question text, because the
       * label is the target entry's own canonical wording resolved server-side, which is what
       * makes clicking one land on that entry rather than near it.
       *
       * ⚠️ THERE IS NO `walkthroughs` MEMBER ANY MORE, and a Function App still sending one is
       * simply ignored — which is the same shape of tolerance as an older one sending fewer.
       * The spotlight tours were retired as a CHIP when the per-screen "?" started running them
       * directly (`components/SupportAssistant/ScreenHelpButton`). All 29 tours are still there;
       * the chip was the only way to reach one, and it could only be found by opening the chat
       * and asking — which is the one place somebody who wanted to be SHOWN a screen was not
       * looking.
       */
      links?: SupportLinkRef[];
      actions?: SupportActionRef[];
      next?: SupportNextRef[];
      /**
       * A create form this answer offers to fill in, with the values the user described.
       *
       * At most ONE, absent for every non-manager (both host screens are manager-only, so the
       * server strips the marker), and absent on a Function App that predates the family. The
       * answer then renders as text alone — nothing here is required.
       */
      settings?: SupportSettingsRef[];
      /**
       * The id of the recorded exchange, posted back if the user rates the answer.
       *
       * ⚠️ ABSENT IS MEANINGFUL AND THE PANEL DEPENDS ON IT: no id means the BFF recorded
       * nothing (`SupportBot__TranscriptsEnabled=N`, a storage failure, or a Function App
       * that predates the feature), so the thumbs are not offered rather than offered and
       * guaranteed to 404.
       */
      id?: string;
      /**
       * Screenshots from the printed CSM guides that this answer refers to, as base64 `data:`
       * URIs. Absent on a Function App that predates them, and on any environment where
       * `SupportBot__HelpImagesEnabled` is off — in both cases the answer simply renders as
       * text, so nothing here is required.
       */
      images?: SupportImage[];
      /**
       * Production orders this answer draws as cards, in slot order.
       *
       * Absent on a Function App that predates them and on any environment where
       * `SupportBot__DataCardsEnabled` is off — and for every non-manager, because the lookup
       * that fills a card is manager-gated. In all of those the answer is the same prose with
       * no marker in it, so nothing here is required.
       */
      cards?: SupportCard[];
    }
  /** 503: this environment's Function App has no model deployment configured. */
  | { kind: "unavailable" }
  /** 429: the model deployment's quota is exhausted for the moment. */
  | { kind: "busy" }
  /** Anything else, including network failure. */
  | { kind: "failed" };

/**
 * Ask the support assistant a question.
 *
 * Uses `config.bffRoot` (the ACTIVE region), not `homeBffRoot`: the question is about the
 * org the user is currently working in, so a CORP user supporting GEORG should be answered
 * by GEORG's Function App with GEORG's identity — the same rule as every data call.
 *
 * `history` is the thread so far, oldest first. The BFF filters it to user/assistant turns
 * and caps the length, so passing the whole conversation is safe.
 *
 * `conversationId` groups the recorded exchanges of one thread. It is generated by the panel
 * and starts again whenever the user clears the chat — nothing on this side reads it back.
 *
 * `context` says where the user is standing (see `SupportChatContext`). It is what lets an
 * answer offer "open the rework form for THIS order" without the user retyping the number.
 *
 * ⚠️ EVERYTHING STILL GOES THROUGH `call()`. That helper attaches the bearer token and the
 * `X-Ambient-Email` impersonation header and owns the 401→renew→retry branch — a CORP user
 * supporting another org must be answered by that org's Function App as that org's identity,
 * and an expiring token must not silently re-run the question in the wrong tenant.
 * `ApiService.supportTenant.test.ts` pins that. Do not reach for `axios` here.
 */
export async function askSupportAssistant(
  auth: AuthContext,
  message: string,
  history: ReadonlyArray<SupportChatTurn> = [],
  conversationId?: string,
  context?: SupportChatContext,
  cancelToken?: CancelToken
): Promise<SupportChatResult> {
  try {
    const val = await call<{
      id?: string;
      answer: string;
      escalate: boolean;
      images?: SupportImage[];
      links?: SupportLinkRef[];
      actions?: SupportActionRef[];
      next?: SupportNextRef[];
      cards?: SupportCard[];
      settings?: SupportSettingsRef[];
    }>(
      `${config.bffRoot}api/support/chat`,
      "POST",
      auth,
      // ⚠️ THE CONTEXT IS SPREAD FLAT, AND IT MUST NOT GO BACK TO BEING NESTED.
      // `SupportChatRequest` in the Function App (`Endpoints/Support/SupportChat.cs`) declares
      // `screenKey`, `productionOrder` and `clockedInJob` at the TOP LEVEL; it has no `context`
      // property at all. Sent nested, all three bound to null on every request and the whole
      // "know what the user is looking at" feature — the foundation the chips were built on —
      // did nothing whatsoever, silently, while both repos' suites stayed green.
      // The grouped `SupportChatContext` parameter is good API for the caller; it just cannot
      // survive into the JSON. Pinned in `ApiService.supportContract.test.ts`.
      { message, history, conversationId, ...(context ?? {}) },
      cancelToken
    );

    return {
      kind: "answer",
      answer: val.data?.answer ?? "",
      escalate: Boolean(val.data?.escalate),
      id: typeof val.data?.id === "string" ? val.data.id : undefined,
      images: Array.isArray(val.data?.images) ? val.data!.images : undefined,
      // Each guarded independently: a Function App that grew one family and not the others
      // must degrade to "that family of chips is absent", never to a render-time TypeError in
      // a panel whose whole job is to be the thing that still works when something is wrong.
      links: Array.isArray(val.data?.links) ? val.data!.links : undefined,
      actions: Array.isArray(val.data?.actions) ? val.data!.actions : undefined,
      next: Array.isArray(val.data?.next) ? val.data!.next : undefined,
      cards: Array.isArray(val.data?.cards) ? val.data!.cards : undefined,
      settings: Array.isArray(val.data?.settings) ? val.data!.settings : undefined,
    };
  } catch (error) {
    // A cancelled request is not a failure — the caller unmounted or asked again.
    if (axios.isCancel(error)) throw new Error(REQUEST_CANCELLED);

    const status = (error as any)?.response?.status;

    // 503: this environment's Function App has no model deployment configured.
    // 404: this environment's Function App predates the endpoint entirely — which is the
    // normal state if the SWA is deployed before the BFF. Both mean "not available here and
    // nothing the user can do", so both retire the panel for the session rather than showing a
    // retryable error that can only fail again.
    if (status === 503 || status === 404) return { kind: "unavailable" };
    if (status === 429) return { kind: "busy" };

    // Deliberately not rethrown: an assistant that fails must never take a screen down with
    // it. Logged so the failure is still visible in App Insights.
    logger.warn("The support assistant could not answer.", error);
    return { kind: "failed" };
  }
}

/** What the reader thought of an answer. `"none"` takes a rating back. */
export type SupportRating = "up" | "down" | "none";

/**
 * Rate one answer (POST api/support/chat/{id}/rating).
 *
 * ⚠️ FIRE AND FORGET, AND THAT IS DELIBERATE. It resolves `false` on any failure and never
 * throws or rethrows. The thumb is an aside to reading an answer the user was happy enough
 * with to react to; a toast saying "couldn't record your rating" over a good answer is worse
 * than silently losing the rating, and there is nothing they could do about it either way.
 * The panel keeps its own optimistic state, so the thumb stays lit regardless.
 *
 * Same region as the question — `config.bffRoot`, not `homeBffRoot`. The exchange was
 * recorded by whichever Function App answered it, and only that one can find it.
 */
export async function rateSupportAnswer(
  auth: AuthContext,
  id: string,
  rating: SupportRating
): Promise<boolean> {
  if (!id) return false;

  try {
    await call<void>(
      `${config.bffRoot}api/support/chat/${encodeURIComponent(id)}/rating`,
      "POST",
      auth,
      { rating }
    );

    return true;
  } catch (error) {
    logger.warn("The support assistant rating could not be recorded.", error);
    return false;
  }
}
/* -----------------------------------------------------------------------------
 * "Describe what you want" (POST api/assist/board-filter, POST api/assist/report)
 *
 * The same Foundry deployment the support assistant uses, asked for a FORM FILLING
 * rather than an answer. Both land on screen unapplied-until-read: a board filter
 * becomes chips beside Clear filters, a report definition opens in the builder
 * unrun and unsaved. Neither endpoint saves, executes or changes anything, and
 * neither answers 403 — see `Endpoints/Assist/` in the Function App repo.
 *
 * ⚠️ BOTH RESULTS ARE VALUES, NOT EXCEPTIONS, for the same reason `askSupportAssistant`
 * is: each failure mode is a different sentence the caller has to say out loud
 * ("not switched on here", "busy", "couldn't reach it"), so throwing would only mean
 * the component re-deriving which case it was from a status code.
 *
 * ⚠️ AND BOTH TREAT 404 AS `unavailable`. A Function App that predates these routes is
 * the normal state while the SWA is deployed ahead of the BFF, and it means exactly
 * what 503 means to the person: not here, nothing you can do. The caller hides the
 * control for the session rather than offering a retry that can only fail again.
 * -------------------------------------------------------------------------- */

/** How the board's date filter should change. `KEEP` leaves it exactly as it is. */
export type AssistDuePreset = "KEEP" | "NONE" | "TODAY" | "THIS_WEEK" | "RANGE";

const ASSIST_DUE_PRESETS: AssistDuePreset[] = [
  "KEEP",
  "NONE",
  "TODAY",
  "THIS_WEEK",
  "RANGE",
];

const isAssistDuePreset = (value: unknown): value is AssistDuePreset =>
  ASSIST_DUE_PRESETS.includes(value as AssistDuePreset);

export type BoardFilterResult =
  | {
      kind: "filter";
      clear: boolean;
      warehouses: string[];
      resources: string[];
      bpNames: string[];
      duePreset: AssistDuePreset;
      /** Inclusive `YYYY-MM-DD`, for `RANGE` only. */
      dueFrom?: string;
      dueTo?: string;
      /** Text for the board's search box. Absent is the ordinary case. */
      text?: string;
      /** What the person named that matched nothing, in their own words. */
      unresolved?: string[];
    }
  /** 200 with nothing to apply: the request was not about filtering this board. */
  | { kind: "refused"; message?: string }
  | { kind: "unavailable" }
  | { kind: "busy" }
  | { kind: "failed" };

/**
 * Ask for a set of board filters.
 *
 * ⚠️ THE PICKERS' OPTIONS ARE SENT WITH THE REQUEST, AND THE REPLY IS CHECKED AGAINST
 * THEM AGAIN LOCALLY (`utility/filterIntent.ts`). The server matches every value to
 * this list and returns it in the list's spelling; the client re-matches because the
 * two lists can legitimately differ by then — BP names are derived from the jobs
 * currently on the board. Neither side may assume the other ran.
 *
 * `config.bffRoot` (the ACTIVE region), like every other data-shaped call: the board
 * being filtered is the one the user is currently connected to.
 */
export async function askBoardFilter(
  auth: AuthContext,
  message: string,
  options: { warehouses: string[]; resources: string[]; bpNames: string[] },
  cancelToken?: CancelToken
): Promise<BoardFilterResult> {
  try {
    const val = await call<{
      clear?: boolean;
      warehouses?: string[];
      resources?: string[];
      bpNames?: string[];
      duePreset?: string;
      dueFrom?: string;
      dueTo?: string;
      text?: string;
      unresolved?: string[];
      message?: string;
    }>(
      `${config.bffRoot}api/assist/board-filter`,
      "POST",
      auth,
      {
        message,
        warehouses: options.warehouses,
        resources: options.resources,
        bpNames: options.bpNames,
      },
      cancelToken
    );

    const data = val.data ?? {};
    const applied =
      Boolean(data.clear) ||
      (data.warehouses?.length ?? 0) > 0 ||
      (data.resources?.length ?? 0) > 0 ||
      (data.bpNames?.length ?? 0) > 0 ||
      (data.duePreset ?? "KEEP") !== "KEEP" ||
      Boolean(data.text) ||
      (data.unresolved?.length ?? 0) > 0;

    // An empty reply carrying a sentence is the server's "that wasn't about this
    // board". It is a 200 on purpose — a status code would make the caller treat a
    // legitimate answer as a fault.
    if (!applied) return { kind: "refused", message: data.message };

    return {
      kind: "filter",
      clear: Boolean(data.clear),
      warehouses: Array.isArray(data.warehouses) ? data.warehouses : [],
      resources: Array.isArray(data.resources) ? data.resources : [],
      bpNames: Array.isArray(data.bpNames) ? data.bpNames : [],
      // Anything the client doesn't know is KEEP — the do-nothing preset. Same
      // polarity as the server's own fallback: an unreadable instruction about dates
      // must never be read as "clear them".
      duePreset: isAssistDuePreset(data.duePreset) ? data.duePreset : "KEEP",
      dueFrom: typeof data.dueFrom === "string" ? data.dueFrom : undefined,
      dueTo: typeof data.dueTo === "string" ? data.dueTo : undefined,
      text: typeof data.text === "string" && data.text ? data.text : undefined,
      unresolved: Array.isArray(data.unresolved) ? data.unresolved : undefined,
    };
  } catch (error) {
    if (axios.isCancel(error)) throw new Error(REQUEST_CANCELLED);

    const status = (error as any)?.response?.status; // eslint-disable-line
    if (status === 503 || status === 404) return { kind: "unavailable" };
    if (status === 429) return { kind: "busy" };

    logger.warn("The board filter could not be worked out.", error);
    return { kind: "failed" };
  }
}

/** One control the dynamic spotlight tour found on screen (`utility/controlScanner.ts`). */
export interface TourControlInput {
  /** The scanner's own id, echoed back. `[a-z0-9]{1,12}`. */
  id: string;
  kind: string;
  label: string;
  context?: string;
  state?: string;
  /** Its `data-csm-step`, if any — recorded with the gap server-side, never sent to the model. */
  anchor?: string;
}

export interface TourControlDescription {
  id: string;
  /** Absent when the model gave none — the tour keeps the control's own label. */
  title?: string;
  body: string;
}

export type TourDescriptionResult =
  | { kind: "described"; controls: TourControlDescription[] }
  | { kind: "unavailable" }
  | { kind: "busy" }
  | { kind: "failed" };

/**
 * CoSMo's text for the dynamic spotlight tour (POST api/assist/describe-controls).
 *
 * ⚠️ EVERY FAILURE IS COSMETIC. The tour already shows text for every step before this
 * answers, so the caller simply keeps it; nothing here is ever surfaced as an error. Same
 * 404-means-unavailable rule as the two calls above, for the same SWA-ahead-of-BFF reason.
 *
 * The reply is filtered to the ids that were sent, again, here — the server does it too, and
 * neither side may assume the other ran.
 */
export async function describeTourControls(
  auth: AuthContext,
  screenKey: string | undefined,
  controls: TourControlInput[],
  cancelToken?: CancelToken
): Promise<TourDescriptionResult> {
  try {
    const val = await call<{ controls?: TourControlDescription[] }>(
      `${config.bffRoot}api/assist/describe-controls`,
      "POST",
      auth,
      { screenKey, controls },
      cancelToken
    );

    const sent = new Set(controls.map((c) => c.id));
    const described = (Array.isArray(val.data?.controls) ? val.data!.controls! : []).filter(
      (c) => c && typeof c.id === "string" && sent.has(c.id) && typeof c.body === "string" && c.body.trim()
    );

    return {
      kind: "described",
      controls: described.map((c) => ({
        id: c.id,
        title: typeof c.title === "string" && c.title.trim() ? c.title.trim() : undefined,
        body: c.body.trim(),
      })),
    };
  } catch (error) {
    if (axios.isCancel(error)) throw new Error(REQUEST_CANCELLED);

    const status = (error as any)?.response?.status; // eslint-disable-line
    if (status === 503 || status === 404) return { kind: "unavailable" };
    if (status === 429) return { kind: "busy" };

    logger.warn("The tour descriptions could not be fetched.", error);
    return { kind: "failed" };
  }
}

/** One display column of a proposed report, qualified by the table it comes from. */
export interface AssistReportFieldRef {
  table: string;
  field: string;
}

/** One summarised column. `aggregate` is Sum/Avg/Min/Max/Count. */
export interface AssistReportMeasureRef extends AssistReportFieldRef {
  aggregate: string;
}

/** One WHERE row. Ids are the builder's own business and are minted on arrival. */
export interface AssistReportConditionRef extends AssistReportFieldRef {
  operator: string;
  value?: unknown;
  group?: string;
  /** A relative-date preset id, never a resolved date. */
  datePreset?: string;
  dateOffset?: number;
}

export interface AssistReportJoinConditionRef {
  leftField: string;
  rightField: string;
  cast?: string;
}

export interface AssistReportJoinRef {
  table: string;
  leftTable?: string;
  type?: string;
  conditions?: AssistReportJoinConditionRef[];
}

export type AssistReportResult =
  | {
      kind: "report";
      name: string;
      table: string;
      select: AssistReportFieldRef[];
      aggregates: AssistReportMeasureRef[];
      conditions: AssistReportConditionRef[];
      joins: AssistReportJoinRef[];
      groupBy: AssistReportFieldRef[];
      /** A `ChartKind`. Presentation only — the chart derives from the query either way. */
      chart?: string;
      /** One sentence for the person: what was built, or what could not be. */
      message?: string;
      /**
       * What was asked for and left out, one plain sentence each — a filter on the wrong
       * kind of column, a link between tables that cannot be matched, a total of
       * something that is not a number. Shown under the builder, because a report that
       * quietly lost a filter looks exactly like one that applied it.
       */
      unresolved?: string[];
    }
  /** 200 with no table: the schema had no way to answer that one. */
  | { kind: "refused"; message?: string }
  | { kind: "unavailable" }
  | { kind: "busy" }
  | { kind: "failed" };

/**
 * Ask for a report definition.
 *
 * `tables` carries the picker's own one-line summaries (`reporting/tableDescriptions.ts`)
 * so the model can tell the sources apart by what they hold rather than by their SQL
 * names. It can only DESCRIBE a table: the set that may be queried is read from the live
 * schema on the server, so a name sent from here that is not a report source is ignored.
 *
 * ⚠️ WHAT COMES BACK IS NOT A `ReportDefinition`. It carries no row ids and its
 * aggregates are a list rather than a keyed record — `fieldKey`'s separator is a control
 * character and stays private to this repo. `reporting/reportIntent.ts` is the half that
 * turns this into builder state.
 */
export async function askReportDefinition(
  auth: AuthContext,
  message: string,
  tables: { name: string; summary?: string; label?: string }[],
  cancelToken?: CancelToken
): Promise<AssistReportResult> {
  // The builder's own words for the columns, and the ones its pickers hold back
  // (`reporting/columnLabels.ts`). Describing and narrowing only — see there.
  const vocabulary = assistVocabulary();

  try {
    const val = await call<{
      name?: string;
      table?: string;
      select?: AssistReportFieldRef[];
      aggregates?: AssistReportMeasureRef[];
      conditions?: AssistReportConditionRef[];
      joins?: AssistReportJoinRef[];
      groupBy?: AssistReportFieldRef[];
      chart?: string;
      message?: string;
      unresolved?: string[];
    }>(
      `${config.bffRoot}api/assist/report`,
      "POST",
      auth,
      {
        message,
        tables,
        columnLabels: vocabulary.labels,
        hiddenColumns: vocabulary.hidden,
      },
      cancelToken
    );

    const data = val.data ?? {};

    // An empty `table` is the server's "I couldn't work out which data that needs".
    // The sentence beside it is the useful part.
    if (!data.table) return { kind: "refused", message: data.message };

    return {
      kind: "report",
      name: typeof data.name === "string" ? data.name : "",
      table: data.table,
      // Each guarded on its own: a Function App that grew one of these and not the
      // others must degrade to "that part of the report is empty", never to a
      // render-time TypeError in the builder.
      select: Array.isArray(data.select) ? data.select : [],
      aggregates: Array.isArray(data.aggregates) ? data.aggregates : [],
      conditions: Array.isArray(data.conditions) ? data.conditions : [],
      joins: Array.isArray(data.joins) ? data.joins : [],
      groupBy: Array.isArray(data.groupBy) ? data.groupBy : [],
      chart: typeof data.chart === "string" ? data.chart : undefined,
      message: typeof data.message === "string" ? data.message : undefined,
      unresolved: Array.isArray(data.unresolved)
        ? data.unresolved.filter((u): u is string => typeof u === "string" && u.trim() !== "")
        : [],
    };
  } catch (error) {
    if (axios.isCancel(error)) throw new Error(REQUEST_CANCELLED);

    const status = (error as any)?.response?.status; // eslint-disable-line
    if (status === 503 || status === 404) return { kind: "unavailable" };
    if (status === 429) return { kind: "busy" };

    logger.warn("The report could not be worked out.", error);
    return { kind: "failed" };
  }
}
